import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Card, CardContent, CardMedia, Container, Grid, Link as MuiLink, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import BreadcrumbsBar from "./BreadcrumbsBar";
import RelatedJobOffers from "./RelatedJobOffers";
import UndrawJobHunt from "icons/undraw_job-hunt_5umi.svg";
import AuthenticationModal from "bundles/Candidate/components/AuthenticationModal";
import sh from "bundles/common/utils/sh";
import FakeCV from "pictures/CvExample.png";
import {
  ARCHITECTURE_CATEGORIES,
  ENGINEERING_CATEGORIES,
  CONSTRUCTION_CATEGORIES,
  ADMINISTRATION_CATEGORIES,
  INDUSTRY_CATEGORIES,
} from "bundles/common/utils/categoryData";

const CATEGORY_MAP = {
  architecture: ARCHITECTURE_CATEGORIES,
  engineering: ENGINEERING_CATEGORIES,
  construction: CONSTRUCTION_CATEGORIES,
  administration: ADMINISTRATION_CATEGORIES,
  industry: INDUSTRY_CATEGORIES,
};

const primaryButtonSx = {
  backgroundColor: "#166D4C",
  color: "white",
  borderRadius: "29px",
  px: { xs: 5, md: 9 },
  py: 1.5,
  fontSize: "14px",
  fontFamily: "Segoe UI Regular, sans-serif",
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: "#135a3d" },
};

const chipButtonSx = {
  borderRadius: "16px",
  px: 2.5,
  py: 1.5,
  textTransform: "none",
  justifyContent: "center",
  fontWeight: 500,
  border: "1px solid #D9E3EE",
  backgroundColor: "white",
  color: "#1F2937",
  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: "#166D4C",
    backgroundColor: "#F5FBF8",
    transform: "translateY(-1px)",
    boxShadow: "0 10px 24px rgba(22, 109, 76, 0.12)",
  },
};

const resolveCategoryLabel = (industry, slug, locale, decap = false) => {
  const list = CATEGORY_MAP[industry]?.[locale] || CATEGORY_MAP[industry]?.en || [];
  const match = list.find((category) => category.slug === slug);

  let label = match ? match.label : slug.replace(/-/g, " ");
  if (decap && !/^[A-Z]{2,}$/.test(label)) label = label.toLocaleLowerCase(locale);
  return label;
};

const NoJobsAvailable = () => {
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const currentCandidate = useSelector((state) => state.current_candidate);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const { industry: routeIndustry, category } = useParams();
  const industry = routeIndustry || window.location.pathname.split("/")[3] || "";

  const [isLoginModalOpened, setIsLoginModalOpened] = useState(false);
  const [loginToAddResume, setLoginToAddResume] = useState(false);
  const [currentTarget, setCurrentTarget] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);

  const categoryLabel = resolveCategoryLabel(industry, category, i18nLocale, true);
  const capCategoryLabel = resolveCategoryLabel(industry, category, i18nLocale);
  const capIndustry = industry.charAt(0).toUpperCase() + industry.slice(1);

  const industryButtons = useMemo(
    () => [
      {
        slug: "architecture",
        label: intl.formatMessage({ id: "company.company_industry.architecture", defaultMessage: "Architecture" }),
      },
      {
        slug: "engineering",
        label: intl.formatMessage({ id: "company.company_industry.engineering", defaultMessage: "Engineering" }),
      },
      {
        slug: "construction",
        label: intl.formatMessage({ id: "company.company_industry.construction", defaultMessage: "Construction" }),
      },
      {
        slug: "administration",
        label: intl.formatMessage({ id: "company.company_industry.administration", defaultMessage: "Administration" }),
      },
      {
        slug: "industry",
        label: intl.formatMessage({ id: "company.company_industry.industry", defaultMessage: "Industry" }),
      },
    ],
    [intl]
  );

  useEffect(() => {
    sh.get("search/blog_posts", {
      params: {
        authenticity_token: authenticityToken,
        filters: { resource_type: "candidate" },
      },
    })
      .then((res) => setBlogPosts(res.data?.data?.blog_posts?.slice(0, 4) || []))
      .catch((err) => console.error(err));

  }, [i18nLocale, authenticityToken]);

  useEffect(() => {
    if (currentCandidate && currentTarget) window.location.href = currentTarget;
  }, [currentCandidate, currentTarget]);

  const handleCreateResumeClicked = (e) => {
    e.preventDefault();
    const target = `/${i18nLocale}/candidate?tab=cv`;
    if (currentCandidate) {
      window.location.href = target;
      return;
    }
    setLoginToAddResume(true);
    setCurrentTarget(target);
    setIsLoginModalOpened(true);
  };

  const onAuthenticationModalClosed = () => {
    setIsLoginModalOpened(false);
    setTimeout(() => setLoginToAddResume(false), 200);
  };

  const crumbs = [
    { label: "Home", path: `/${i18nLocale}` },
    { label: "Job Offers", path: `/${i18nLocale}/job-offers` },
    { label: capIndustry, path: `/${i18nLocale}/job-offers/${industry}` },
    { label: capCategoryLabel, current: true },
  ];

  return (
    <>
      <Box
        sx={{
          background: "linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)",
          pt: { xs: 6, md: 8 },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <BreadcrumbsBar crumbs={crumbs} />

          <Grid container spacing={5} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={12} md={7}>
              <Typography variant="h1" sx={{ mb: 2, fontSize: { xs: "2rem", md: "2.75rem" }, lineHeight: 1.15 }}>
                <FormattedMessage id="public_job_offer_index.no_jobs_available.title" values={{ category: categoryLabel }} />
              </Typography>
              <Typography variant="body1" sx={{ color: "#4B5563", maxWidth: 680, mb: 4 }}>
                <FormattedMessage id="public_job_offer_index.first_block_intro" />
              </Typography>
              <Button variant="contained" href={`/${i18nLocale}/job-offers`} sx={primaryButtonSx}>
                <FormattedMessage id="public_job_offer_index.no_jobs_available.view_all_jobs" />
              </Button>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  borderRadius: 4,
                  backgroundColor: "white",
                  border: "1px solid #E6EBF1",
                  p: { xs: 3, md: 4 },
                }}
              >
                <Box component="img" src={UndrawJobHunt} alt="Job hunt illustration" sx={{ width: "100%", maxHeight: 300 }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: "white", pt: { xs: 2, md: 2.5 }, pb: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" sx={{ mb: 3, fontSize: { xs: "1.6rem", md: "2rem" } }}>
            <FormattedMessage id="candidate_search.sectors" />
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {industryButtons.map(({ slug, label }) => (
              <Grid item xs={6} md={2.4} key={slug}>
                <Button fullWidth variant="outlined" href={`/${i18nLocale}/job-offers/${slug}`} sx={chipButtonSx}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.25, width: "100%" }}>
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: "13px", md: "14px" },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {label}
                    </Box>
                  </Box>
                </Button>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: "#F7FAFD", py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            align="center"
            sx={{
              display: "inline-flex",
              mb: 3,
              px: 1.5,
              py: 0.5,
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "#166D4C",
              backgroundColor: "#E9F7F0",
              position: "relative",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            Guide m&eacute;tier
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: 4,
                  border: "1px solid #E6EBF1",
                  p: { xs: 3, md: 4 },
                  height: "100%",
                  boxShadow: "0 6px 20px rgba(15, 23, 42, 0.05)",
                }}
              >
                <Typography variant="h3" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  <FormattedMessage id="public_job_offer_index.no_jobs_available.job_responsibilities" />
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", pl: 0, m: 0, color: "#374151" }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                    <li key={`responsibility-${num}`} style={{ marginBottom: "10px" }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                        <Box
                          component="span"
                          sx={{
                            minWidth: 22,
                            height: 22,
                            borderRadius: "50%",
                            backgroundColor: "#E9F7F0",
                            color: "#166D4C",
                            fontSize: "12px",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mt: "2px",
                          }}
                        >
                          {num}
                        </Box>
                        <Box sx={{ lineHeight: 1.6 }}>
                          <FormattedMessage id={`public_job_offer_index.no_jobs_available.industry_skills.${industry}.responsibilities.${num}`} />
                        </Box>
                      </Box>
                    </li>
                  ))}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: 4,
                  border: "1px solid #E6EBF1",
                  p: { xs: 3, md: 4 },
                  height: "100%",
                  boxShadow: "0 6px 20px rgba(15, 23, 42, 0.05)",
                }}
              >
                <Typography variant="h3" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  <FormattedMessage id="public_job_offer_index.no_jobs_available.core_skills" />
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", pl: 0, m: 0, color: "#374151" }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                    <li key={`skill-${num}`} style={{ marginBottom: "10px" }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                        <Box
                          component="span"
                          sx={{
                            minWidth: 22,
                            height: 22,
                            borderRadius: "50%",
                            backgroundColor: "#E9F7F0",
                            color: "#166D4C",
                            fontSize: "12px",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mt: "2px",
                          }}
                        >
                          {num}
                        </Box>
                        <Box sx={{ lineHeight: 1.6 }}>
                          <FormattedMessage id={`public_job_offer_index.no_jobs_available.industry_skills.${industry}.skills.${num}`} />
                        </Box>
                      </Box>
                    </li>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 4, md: 5 }, backgroundColor: "#166D4C" }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                sx={{
                  display: "inline-flex",
                  mb: 2,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#166D4C",
                  backgroundColor: "white",
                }}
              >
                CV Builder
              </Typography>
              <Typography
                variant="h2"
                sx={{ mb: 1.5, fontSize: { xs: "1.5rem", md: "1.9rem" }, color: "white" }}
              >
                <FormattedMessage id="public_job_offer_index.no_jobs_available.need_cv" />
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.92)", mb: 2.5, fontSize: "15px" }}>
                <FormattedMessage id="public_job_offer_index.no_jobs_available.cv_description" />
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0, mb: 3, color: "rgba(255,255,255,0.92)", lineHeight: 1.65, fontSize: "14px" }}>
                <li><FormattedMessage id="generate_cv.point_1" /></li>
                <li><FormattedMessage id="generate_cv.point_2" /></li>
                <li><FormattedMessage id="generate_cv.point_3" /></li>
              </Box>
              <Button
                variant="contained"
                onClick={handleCreateResumeClicked}
                sx={{
                  ...primaryButtonSx,
                  backgroundColor: "white",
                  color: "#166D4C",
                  "&:hover": { backgroundColor: "#ECFDF3" },
                }}
              >
                <FormattedMessage id="public_job_offer_index.no_jobs_available.try_cv_tool" />
              </Button>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src={FakeCV}
                alt={intl.formatMessage({ id: "generate_cv.alt" })}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "260px", md: "320px" },
                  mx: "auto",
                  display: "block",
                  borderRadius: 2.5,
                  border: "1px solid rgba(255,255,255,0.25)",
                  boxShadow: "0 10px 24px rgba(2, 6, 23, 0.22)",
                  backgroundColor: "white",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: "#F7FAFD", py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" sx={{ mb: 5, fontSize: { xs: "1.7rem", md: "2rem" } }}>
            <FormattedMessage id="public_job_offer_index.no_jobs_available.latest_blog" />
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {blogPosts.map((post) => (
              <Grid item xs={12} sm={6} md={3} key={post.id}>
                <MuiLink
                  href={`/${i18nLocale}/candidates/posts/${post.encoded_title}/${post.id}`}
                  underline="none"
                  sx={{ display: "block", height: "100%" }}
                >
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      border: "1px solid #E3EAF3",
                      boxShadow: "0 4px 16px rgba(15, 23, 42, 0.06)",
                      transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        borderColor: "#166D4C",
                        boxShadow: "0 16px 30px rgba(22,109,76,0.14)",
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={post.cover_picture_url}
                      alt={post.title}
                      sx={{ height: 152, objectFit: "cover" }}
                    />
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mb: 1 }}>
                        {new Date(post.created_at).toLocaleDateString(i18nLocale)}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#0F172A",
                          fontWeight: 700,
                          lineHeight: 1.35,
                          mb: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {post.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#475569",
                          mb: 2,
                          minHeight: 42,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {(post.content || "").replace(/<[^>]+>/g, "").trim()}
                      </Typography>
                      <Typography sx={{ color: "#166D4C", fontWeight: 700, fontSize: "14px" }}>
                        <FormattedMessage id="blog.read_article" defaultMessage="Read article" />
                      </Typography>
                    </CardContent>
                  </Card>
                </MuiLink>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
            <Button variant="contained" href={`/${i18nLocale}/candidates/posts`} sx={primaryButtonSx}>
              <FormattedMessage id="landing_candidate.view_our_advises" />
            </Button>
          </Box>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: "white", py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <RelatedJobOffers />
        </Container>
      </Box>

      <AuthenticationModal
        isOpen={isLoginModalOpened}
        section="login_or_signin"
        closeModal={onAuthenticationModalClosed}
        addResumeLogin={loginToAddResume}
      />
    </>
  );
};

export default NoJobsAvailable;
